import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import {
    CPM_QUEUE_PAGE_SIZE,
    CPM_QUEUE_TAB,
    CPM_TAB_TO_QUEUE_TYPE,
} from '../../constants/centralProductMaster.constants';
import { getQueueCounts, listVerificationQueue } from '../../services/productMasterService';
import type {
    CpmBarcodeConflictItem,
    CpmLinkReviewItem,
    CpmPendingItem,
    CpmPendingView,
    CpmQueueCountsResponse,
    CpmQueueFilters,
    CpmQueueListRequest,
    CpmQueueTab,
} from '../../types/centralProductMaster.types';

/** Same intents as Master Editor list loading UX. */
type QueueListIntent = 'replace' | 'page';

function defaultFilters(): CpmQueueFilters {
    return {
        band: [],
        method: [],
        recordType: [],
        signals: [],
        storeBrand: null,
        storeBrandLabel: null,
        disputed: null,
        dateFrom: null,
        dateTo: null,
        sortBy: 'createdAt',
        sortDir: 'desc',
        page: 1,
        limit: CPM_QUEUE_PAGE_SIZE,
    };
}

interface TabBucket {
    items: Array<CpmPendingItem | CpmLinkReviewItem | CpmBarcodeConflictItem>;
    pagination: { page: number; limit: number; total: number; totalPages: number } | null;
    /** Filtered matching record count (pending: pre-group union). */
    recordTotal: number | null;
    loading: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
}

interface CpmVerificationQueueState {
    activeTab: CpmQueueTab;
    pendingView: CpmPendingView;
    filtersByTab: Record<CpmQueueTab, CpmQueueFilters>;
    lists: Record<CpmQueueTab, TabBucket>;
    /** Queue size of the active tab, filters excluded — comes with its list response. */
    activeTabTotal: number | null;
    /** The two header stats. Their own GET: they span all three queues. */
    counts: CpmQueueCountsResponse | null;
    selectedIds: string[];
    /** Full rows for selectedIds so bulk actions still work after search / page change. */
    selectedItemsById: Record<string, CpmPendingItem | CpmLinkReviewItem | CpmBarcodeConflictItem>;
    /** Drives skeleton vs keep-table page-busy UX (mirrors Master Editor). */
    listIntent: QueueListIntent;
    pageBusyLabel: string;
}

export type QueueSelectedItem = CpmPendingItem | CpmLinkReviewItem | CpmBarcodeConflictItem;

export function queueSelectionKey(item: QueueSelectedItem): string {
    return '_id' in item ? item._id : item.barcode;
}

function resetSelection(state: CpmVerificationQueueState) {
    state.selectedIds = [];
    state.selectedItemsById = {};
}

const emptyBucket = (): TabBucket => ({
    items: [],
    pagination: null,
    recordTotal: null,
    loading: 'idle',
    error: null,
});

const initialState: CpmVerificationQueueState = {
    activeTab: CPM_QUEUE_TAB.pending,
    pendingView: 'grouped',
    filtersByTab: {
        pending: defaultFilters(),
        link: { ...defaultFilters(), sortBy: 'confidence' },
        barcode: { ...defaultFilters(), sortBy: 'firstConflictAt' },
    },
    lists: {
        pending: emptyBucket(),
        link: emptyBucket(),
        barcode: emptyBucket(),
    },
    activeTabTotal: null,
    counts: null,
    selectedIds: [],
    selectedItemsById: {},
    listIntent: 'replace',
    pageBusyLabel: '',
};

function applyPageIntent(state: CpmVerificationQueueState, label: string) {
    state.listIntent = 'page';
    state.pageBusyLabel = label;
}

function applyReplaceIntent(state: CpmVerificationQueueState) {
    state.listIntent = 'replace';
    state.pageBusyLabel = '';
}

function buildListBody(
    tab: CpmQueueTab,
    filters: CpmQueueFilters,
    pendingView: CpmPendingView
): CpmQueueListRequest {
    const body: CpmQueueListRequest = {
        type: CPM_TAB_TO_QUEUE_TYPE[tab],
        page: filters.page,
        limit: filters.limit,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
    };

    if (tab !== 'barcode' && filters.method.length) body.method = filters.method;
    if (tab !== 'barcode' && filters.recordType.length) body.recordType = filters.recordType;
    if (filters.storeBrand) body.storeBrand = filters.storeBrand;
    if (filters.dateFrom) body.dateFrom = filters.dateFrom;
    if (filters.dateTo) body.dateTo = filters.dateTo;

    if (tab === 'link' && filters.band.length) body.band = filters.band;
    if (tab === 'pending' && filters.signals.length) body.signals = filters.signals;
    if (tab === 'barcode' && filters.disputed != null) body.disputed = filters.disputed;
    if (tab === 'pending') body.groupByChain = pendingView === 'grouped';

    return body;
}

export const fetchVerificationQueue = createAsyncThunk(
    'cpmVerificationQueue/fetchList',
    async (tab: CpmQueueTab, { getState }) => {
        const state = getState() as RootState;
        const filters = state.cpmVerificationQueue.filtersByTab[tab];
        const pendingView = state.cpmVerificationQueue.pendingView;
        const data = await listVerificationQueue(buildListBody(tab, filters, pendingView));
        return { tab, data };
    }
);

export const fetchQueueCounts = createAsyncThunk('cpmVerificationQueue/fetchCounts', async () =>
    getQueueCounts()
);

const cpmVerificationQueueSlice = createSlice({
    name: 'cpmVerificationQueue',
    initialState,
    reducers: {
        setActiveTab(state, action: PayloadAction<CpmQueueTab>) {
            state.activeTab = action.payload;
            state.activeTabTotal = null;
            resetSelection(state);
            applyReplaceIntent(state);
        },
        setPendingView(state, action: PayloadAction<CpmPendingView>) {
            state.pendingView = action.payload;
            state.filtersByTab.pending.page = 1;
            applyPageIntent(state, 'Switching view');
        },
        patchFilters(
            state,
            action: PayloadAction<{ tab: CpmQueueTab; patch: Partial<CpmQueueFilters> }>
        ) {
            const { tab, patch } = action.payload;
            const keys = Object.keys(patch).filter((k) => k !== 'page');
            state.filtersByTab[tab] = {
                ...state.filtersByTab[tab],
                ...patch,
                page: patch.page ?? 1,
            };

            if (
                keys.length === 1 &&
                (keys[0] === 'limit' || keys[0] === 'sortBy' || keys[0] === 'sortDir')
            ) {
                applyPageIntent(
                    state,
                    keys[0] === 'limit' ? 'Updating page size' : 'Sorting records'
                );
            } else if (keys.length === 0 && patch.page != null) {
                applyPageIntent(state, 'Fetching page');
            } else {
                applyPageIntent(state, 'Applying filters');
            }
        },
        setPage(state, action: PayloadAction<{ tab: CpmQueueTab; page: number }>) {
            state.filtersByTab[action.payload.tab].page = action.payload.page;
            applyPageIntent(state, 'Fetching page');
        },
        toggleSelectedItem(state, action: PayloadAction<QueueSelectedItem>) {
            const item = action.payload;
            const id = queueSelectionKey(item);
            if (state.selectedIds.includes(id)) {
                state.selectedIds = state.selectedIds.filter((x) => x !== id);
                delete state.selectedItemsById[id];
            } else {
                state.selectedIds.push(id);
                state.selectedItemsById[id] = item;
            }
        },
        setPageSelection(
            state,
            action: PayloadAction<{ items: QueueSelectedItem[]; checked: boolean }>
        ) {
            const { items, checked } = action.payload;
            if (checked) {
                for (const item of items) {
                    const id = queueSelectionKey(item);
                    if (!state.selectedIds.includes(id)) state.selectedIds.push(id);
                    state.selectedItemsById[id] = item;
                }
                return;
            }
            const drop = new Set(items.map(queueSelectionKey));
            state.selectedIds = state.selectedIds.filter((id) => !drop.has(id));
            for (const id of drop) delete state.selectedItemsById[id];
        },
        clearSelection(state) {
            resetSelection(state);
        },
        clearFilters(state, action: PayloadAction<CpmQueueTab>) {
            const tab = action.payload;
            const defaults = defaultFilters();
            if (tab === 'link') defaults.sortBy = 'confidence';
            if (tab === 'barcode') defaults.sortBy = 'firstConflictAt';
            // Preserve page size when clearing filters (same as Master Editor).
            defaults.limit = state.filtersByTab[tab].limit;
            state.filtersByTab[tab] = defaults;
            resetSelection(state);
            applyPageIntent(state, 'Clearing filters');
        },
        /** Full skeleton reload (e.g. error retry). */
        beginListReload(state, action: PayloadAction<string | undefined>) {
            applyReplaceIntent(state);
            if (action.payload) {
                // label unused for replace; kept for call-site clarity
                void action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerificationQueue.pending, (state, action) => {
                const tab = action.meta.arg;
                state.lists[tab].loading = 'loading';
                state.lists[tab].error = null;
                if (state.listIntent === 'replace') {
                    state.lists[tab].items = [];
                    state.lists[tab].pagination = null;
                    state.lists[tab].recordTotal = null;
                }
            })
            .addCase(fetchVerificationQueue.fulfilled, (state, action) => {
                const { tab, data } = action.payload;
                state.lists[tab].loading = 'success';
                state.lists[tab].items = data.items;
                state.lists[tab].pagination = data.pagination;
                state.lists[tab].recordTotal =
                    data.recordTotal != null ? data.recordTotal : data.pagination.total;
                if (tab === state.activeTab) {
                    state.activeTabTotal = data.queueTotal;
                }
                state.pageBusyLabel = '';
            })
            .addCase(fetchVerificationQueue.rejected, (state, action) => {
                const tab = action.meta.arg;
                state.lists[tab].loading = 'error';
                state.lists[tab].error = action.error.message || 'Failed to load queue';
                state.pageBusyLabel = '';
            })
            .addCase(fetchQueueCounts.fulfilled, (state, action) => {
                state.counts = action.payload;
            });
    },
});

export const {
    setActiveTab,
    setPendingView,
    patchFilters,
    clearFilters,
    setPage,
    toggleSelectedItem,
    setPageSelection,
    clearSelection,
    beginListReload,
} = cpmVerificationQueueSlice.actions;

export const useCpmVerificationQueue = () =>
    useSelector((state: RootState) => state.cpmVerificationQueue);

export default cpmVerificationQueueSlice.reducer;
